<template>
  <div class="child-route-wrapper complete-info-wrapper">
    <h1 class="registration-form-title">
      Complete your profile
    </h1>
    <validation-observer
      #default="{ validate }"
      class="observer-wrapper"
    >
      <b-form
        class="registration-form-wrapper"
        @submit.prevent="onSubmit(validate)"
      >
        <b-form-group
          class="registration-input-group"
          label="First name"
          label-class="registration-label"
        >
          <validation-provider
            #default="{ errors, valid }"
            name="First name"
            :rules="stepData[AUTH_REGISTER_V2_CHILDREN.COMPLETE_INFO].fields.firstName"
          >
            <b-form-input
              v-model="firstName"
              class="registration-input"
              :class="{ 'is-invalid': errors.length }"
              :state="valid || firstName === '' ? null : false"
              type="text"
              placeholder="First name"
            />
            <div class="input-error">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </validation-provider>
        </b-form-group>
        <b-form-group
          class="registration-input-group"
          label="Last name"
          label-class="registration-label"
        >
          <validation-provider
            #default="{ errors, valid }"
            name="Last name"
            :rules="stepData[AUTH_REGISTER_V2_CHILDREN.COMPLETE_INFO].fields.lastName"
          >
            <b-form-input
              v-model="lastName"
              class="registration-input"
              :class="{ 'is-invalid': errors.length }"
              :state="valid || lastName === '' ? null : false"
              type="text"
              placeholder="Last name"
            />
            <div class="input-error">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </validation-provider>
        </b-form-group>
        <label class="registration-label mb-0">
          Mobile number
        </label>
        <div class="phone-row">
          <div class="country-code-wrapper">
            <validation-provider
              #default="{ errors }"
              name="Country code"
              :rules="stepData[AUTH_REGISTER_V2_CHILDREN.COMPLETE_INFO].fields.country"
            >
              <b-form-group>
                <v-select
                  v-model="country"
                  append-to-body
                  :calculate-position="withPopper"
                  class="country-code-dropdown"
                  :class="{ 'is-invalid': errors.length }"
                  placeholder="Code"
                  :clearable="false"
                  :options="countryCodeOptions"
                  @input="onInputCountryCode"
                  @search="handleCountryCodeSearch"
                >
                  <template #open-indicator="{ attributes }">
                    <dropdown-icon v-bind="attributes" />
                  </template>
                  <template #selected-option="{ flag, dial_code }">
                    <span class="custom-option">{{ flag }}</span>{{ dial_code }}
                  </template>
                  <template #option="{ flag, dial_code }">
                    <span class="custom-option">{{ flag }}</span>{{ dial_code }}
                  </template>
                </v-select>
                <div class="input-error">
                  <small class="text-danger">{{ errors[0] }}</small>
                </div>
              </b-form-group>
            </validation-provider>
          </div>
          <div class="phone-number-wrapper">
            <validation-provider
              #default="{ errors, valid }"
              name="Mobile number"
              :rules="stepData[AUTH_REGISTER_V2_CHILDREN.COMPLETE_INFO].fields.phone"
            >
              <b-form-group class="registration-input-group">
                <b-form-input
                  v-model="phone"
                  class="registration-input"
                  :class="{ 'is-invalid': errors.length }"
                  :state="valid || phone === '' ? null : false"
                  type="tel"
                  name="phone"
                  placeholder="Mobile number"
                  @input="onInputPhoneNumber"
                  @keypress="onlyAllowPositiveInteger"
                  @paste="handlePaste"
                />
                <div class="input-error">
                  <small class="text-danger">{{ errors[0] || customPhoneError }}</small>
                </div>
              </b-form-group>
            </validation-provider>
          </div>
        </div>
        <b-button
          class="registration-button"
          type="submit"
        >
          Next
        </b-button>
      </b-form>
    </validation-observer>
  </div>
</template>
<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BForm, BFormGroup, BFormInput, BButton,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import DropdownIcon from '@/@core/assets/svg-components/DropdownIcon.vue'
import { useFormFields } from '@/views/apps/authentication-v2/useFormFields'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { AUTH_REGISTER_V2_CHILDREN } from '@/router/routes/utils'
import registerModule from '@/store/modules/register.module'
import { createPopper } from '@popperjs/core'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { watch } from '@vue/composition-api'
import { debounce } from 'lodash'
import { onlyAllowPositiveInteger } from '../../product/edit/utils'
import { stepData } from './verifySteps'

export default {
  name: 'CompleteInfo',
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    DropdownIcon,
    BButton,
  },
  data() {
    return {
      customPhoneError: '',
      countryCodeOptions: registerModule.state.allCountryCodes,
      stepData,
      AUTH_REGISTER_V2_CHILDREN,
    }
  },
  setup() {
    const {
      firstName,
      lastName,
      country,
      phone,
    } = useFormFields()

    const trackInputFields = debounce((newFirstName, newLastName, newCountry, newPhone) => {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.COMPLETE_PROFILE_ENTERS_DATA, {
        formData: {
          firstName: newFirstName,
          lastName: newLastName,
          countryCode: newCountry?.dial_code,
          phone: newPhone,
        },
      })
    }, 1000)

    watch(
      [firstName, lastName, country, phone],
      ([newFirstName, newLastName, newCountry, newPhone]) => {
        trackInputFields(newFirstName, newLastName, newCountry, newPhone)
      },
    )

    return {
      firstName,
      lastName,
      country,
      phone,
    }
  },
  mounted() {
    analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.COMPLETE_PROFILE_VIEW)
  },
  methods: {
    onlyAllowPositiveInteger,
    withPopper(dropdownList, component, { width }) {
      dropdownList.style.width = width

      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: 'bottom',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -1],
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top'],
              padding: 10, // Give some space from the edge of the viewport
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 10,
            },
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({ state }) {
              component.$el.classList.toggle(
                'drop-up',
                state.placement === 'top',
              )
            },
          },
        ],
      })

      return () => popper.destroy()
    },
    getPhoneError() {
      return this.country && isValidPhoneNumber(`${this.country.dial_code}${this.phone}`) ? '' : 'Please enter the valid phone number'
    },
    async onSubmit(validate) {
      this.customPhoneError = this.getPhoneError()
      const areOtherFieldsValid = await validate()
      if (areOtherFieldsValid && !this.customPhoneError) {
        analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.COMPLETE_PROFILE_NEXT_CLICK, {
          formData: {
            firstName: this.firstName,
            lastName: this.lastName,
            countryCode: this.country?.dial_code,
            phone: this.phone,
          },
        })
        this.$router.push({ name: AUTH_REGISTER_V2_CHILDREN.COMPANY_INFO })
      }
    },
    handlePaste(event) {
      // Prevent the default paste
      event.preventDefault()

      // Get pasted data
      const pastedData = (event.clipboardData || window.clipboardData).getData('text')

      // Clean the data to only include numbers
      const cleanedData = pastedData.replace(/[^0-9]/g, '')

      // Insert the cleaned data
      this.phone += cleanedData
    },
    onInputCountryCode(code) {
      this.country = code
      setTimeout(() => document.activeElement.blur(), 50)
      if (this.phone) {
        this.customPhoneError = this.getPhoneError()
      }
    },
    handleCountryCodeSearch(searchString) {
      this.countryCodeOptions = registerModule.state.allCountryCodes.filter(country => country.dial_code.toLowerCase().includes(searchString.toLowerCase()))
    },
    onInputPhoneNumber(number) {
      // Clean non-numeric characters on input (handles autofill)
      if (number) {
        // Keep only numeric characters
        const cleanedNumber = number.replace(/[^0-9]/g, '')

        // Update the model if the cleaned number is different
        if (cleanedNumber !== number) {
          this.$nextTick(() => {
            this.phone = cleanedNumber
          })
        }
      }

      this.customPhoneError = this.getPhoneError()
    },
  },
}
</script>
<style lang="scss">
@import '../common-styles.scss';
@import url('https://fonts.googleapis.com/css2?family=Noto+Color+Emoji&display=swap');

.complete-info-wrapper {
  margin-top: 2rem;
}

/* Global styles for vue-select dropdown when appended to body */
body .vs__dropdown-menu {
  max-height: 200px;
  overflow-y: auto;
  font-size: 20px;
}

.margin-top {
  margin-top: 5rem;
}

.observer-wrapper {
  width: 100%;
}

.phone-row {
  display: flex;
  gap: 16px;
}

.country-code-wrapper {
  width: 142px;
}

.phone-number-wrapper {
  width: calc(100% - 152px);
}

.custom-option {
  font-size: 30px;
  line-height: 20px;
  font-family: "Noto Color Emoji", sans-serif;
}

.country-code-dropdown {
  font-size: 20px;
  line-height: 24px;
  height: 48px;

  .vs__dropdown-toggle {
    height: 100%;
    background-color: #fff;
  }

  input::placeholder {
    color: #6C757D;
    font-weight: 400;
  }

  .vs__selected {
    transform: none !important;
    margin-top: 4px !important;
  }
}

.country-code-dropdown.is-invalid {
  .vs__dropdown-toggle {
    border: 1px solid red;
  }
}

.country-code-dropdown.vs--open {
  .vs__open-indicator {
    transform: rotate(180deg) translateY(3px);
  }
}

@media screen and (max-width: 660px) {
  .country-code-dropdown {
    font-size: 16px;
    line-height: 24px;
    height: 38px;

    .vs__selected,
    .vs__actions {
      margin: 0;
      margin-top: 3px !important;
    }

    .vs__dropdown-menu {
      font-size: 16px;
    }
  }

  .vs--single.vs--open {
    .vs__selected {
      height: 34px;
    }
  }

  .country-code-wrapper {
    width: 118px;
  }

  .phone-number-wrapper {
    width: calc(100% - 118px);
  }

  .custom-option {
    font-size: 24px;
    line-height: normal;
  }
}

@media screen and (max-width: 425px) {
  .phone-number-wrapper {
    width: 100%;
  }
}
</style>
